import { useEffect, useState } from 'react';
import { Search, Filter, TrendingUp, TrendingDown, Star, ChevronDown, SlidersHorizontal, ArrowUpDown, X, Zap, Target, DollarSign, BarChart3, Activity, TrendingUp as Momentum, Globe, PieChart, RefreshCcw, Magnet, LineChart, ShoppingCart, Layers } from 'lucide-react';
import { Stock } from '../lib/supabase';
import { marketService } from '../lib/marketService';
import { useAuth } from '../contexts/AuthContext';

interface StockScreenerProps {
  onStockClick: (stock: Stock) => void;
}

type SortField = 'code' | 'price' | 'change' | 'volume' | 'market_cap' | 'pe_ratio';
type SortOrder = 'asc' | 'desc';

type ScreenerPreset =
  | 'all'
  | 'top_broker_accum'
  | 'smart_money'
  | 'high_vol_breakout'
  | 'value_momentum'
  | 'bullish_momentum'
  | 'net_foreign_buy'
  | 'net_income_growth'
  | 'reversal_potential'
  | 'oversold_bounce'
  | 'price_magnet'
  | 'stoch_rsi_golden'
  | 'strong_buy_pressure'
  | 'haka_screener';

const SCREENER_PRESETS: { id: ScreenerPreset; name: string; icon: typeof Zap; description: string }[] = [
  { id: 'all', name: 'Semua Saham', icon: Layers, description: 'Tampilkan semua saham' },
  { id: 'top_broker_accum', name: 'Top Broker Accum', icon: Target, description: 'Saham dengan akumulasi broker tertinggi' },
  { id: 'smart_money', name: 'Follow Smart Money', icon: DollarSign, description: 'Ikuti aliran dana institusi besar' },
  { id: 'high_vol_breakout', name: 'High Vol Breakout', icon: Zap, description: 'Volume tinggi dengan breakout harga' },
  { id: 'value_momentum', name: 'Value Momentum', icon: BarChart3, description: 'Saham murah dengan momentum naik' },
  { id: 'bullish_momentum', name: 'Bullish Momentum', icon: Momentum, description: 'Momentum bullish kuat' },
  { id: 'net_foreign_buy', name: 'Net Foreign Buy', icon: Globe, description: 'Net beli asing positif' },
  { id: 'net_income_growth', name: 'Net Income Growth', icon: PieChart, description: 'Pertumbuhan laba bersih tinggi' },
  { id: 'reversal_potential', name: 'Reversal Potential', icon: RefreshCcw, description: 'Potensi reversal dari downtrend' },
  { id: 'oversold_bounce', name: 'Oversold Bounce', icon: Activity, description: 'RSI oversold siap bounce' },
  { id: 'price_magnet', name: 'Price Magnet', icon: Magnet, description: 'Harga mendekati resistance kuat' },
  { id: 'stoch_rsi_golden', name: 'Stoch RSI Golden Cross', icon: LineChart, description: 'Golden cross pada Stochastic RSI' },
  { id: 'strong_buy_pressure', name: 'Strong Buy Pressure', icon: ShoppingCart, description: 'Tekanan beli sangat kuat' },
  { id: 'haka_screener', name: 'HAKA Screener', icon: BarChart3, description: 'Hajar Kanan - Bid dominan' },
];

export const StockScreener = ({ onStockClick }: StockScreenerProps) => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [filteredStocks, setFilteredStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('market_cap');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [activePreset, setActivePreset] = useState<ScreenerPreset>('all');
  const { user } = useAuth();

  const [filters, setFilters] = useState({
    sector: '',
    minPrice: '',
    maxPrice: '',
    minPE: '',
    maxPE: '',
    minROE: '',
    minVolume: '',
  });

  useEffect(() => {
    loadStocks();
    if (user) loadWatchlist();
  }, [user]);

  useEffect(() => {
    filterAndSortStocks();
  }, [stocks, searchTerm, filters, sortField, sortOrder, activePreset]);

  const loadStocks = async () => {
    try {
      const data = await marketService.getStocks();
      setStocks(data);
    } catch (error) {
      console.error('Error loading stocks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWatchlist = async () => {
    if (!user) return;
    try {
      const data = await marketService.getWatchlist(user.id);
      setWatchlist(data.map(w => w.stock_code));
    } catch (error) {
      console.error('Error loading watchlist:', error);
    }
  };

  const applyPresetFilter = (stockList: Stock[]): Stock[] => {
    switch (activePreset) {
      case 'top_broker_accum':
        return stockList.filter(s => s.broker_net_buy > 0).sort((a, b) => b.broker_net_buy - a.broker_net_buy);

      case 'smart_money':
        return stockList.filter(s =>
          s.broker_net_buy > 0 &&
          s.volume_ratio > 1.5 &&
          s.market_cap > 1_000_000_000_000
        );

      case 'high_vol_breakout':
        return stockList.filter(s =>
          s.volume_ratio > 2 &&
          s.price > s.prev_close &&
          s.price >= s.high * 0.98
        );

      case 'value_momentum':
        return stockList.filter(s =>
          s.pe_ratio && s.pe_ratio < 15 &&
          s.pb_ratio && s.pb_ratio < 1.5 &&
          s.momentum_score > 50
        );

      case 'bullish_momentum':
        return stockList.filter(s =>
          s.macd_signal === 'bullish' &&
          s.momentum_score > 60 &&
          marketService.calculateChangePercent(s.price, s.prev_close) > 0
        );

      case 'net_foreign_buy':
        return stockList.filter(s => s.foreign_net_buy > 0).sort((a, b) => b.foreign_net_buy - a.foreign_net_buy);

      case 'net_income_growth':
        return stockList.filter(s =>
          s.eps_growth && s.eps_growth > 10 &&
          s.revenue_growth && s.revenue_growth > 5
        ).sort((a, b) => (b.eps_growth || 0) - (a.eps_growth || 0));

      case 'reversal_potential':
        return stockList.filter(s =>
          s.rsi && s.rsi < 40 &&
          s.price < s.prev_close &&
          s.macd_signal !== 'bearish' &&
          s.support && s.price <= s.support * 1.05
        );

      case 'oversold_bounce':
        return stockList.filter(s =>
          s.rsi && s.rsi < 30 &&
          s.stoch_rsi && s.stoch_rsi < 20
        ).sort((a, b) => (a.rsi || 0) - (b.rsi || 0));

      case 'price_magnet':
        return stockList.filter(s =>
          s.resistance &&
          s.price >= s.resistance * 0.95 &&
          s.price < s.resistance
        );

      case 'stoch_rsi_golden':
        return stockList.filter(s =>
          s.stoch_rsi && s.stoch_rsi > 20 && s.stoch_rsi < 50 &&
          s.rsi && s.rsi > 30 && s.rsi < 50 &&
          s.macd_signal === 'bullish'
        );

      case 'strong_buy_pressure':
        return stockList.filter(s =>
          s.volume_ratio > 1.5 &&
          marketService.calculateChangePercent(s.price, s.prev_close) > 2 &&
          s.price >= s.high * 0.98
        );

      case 'haka_screener':
        return stockList.filter(s =>
          s.volume_ratio > 1.2 &&
          s.broker_net_buy > 0 &&
          marketService.calculateChangePercent(s.price, s.prev_close) > 0 &&
          s.price > s.open_price
        ).sort((a, b) => b.volume_ratio - a.volume_ratio);

      default:
        return stockList;
    }
  };

  const filterAndSortStocks = () => {
    let result = stocks;

    if (searchTerm) {
      result = result.filter(
        stock =>
          stock.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stock.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filters.sector) result = result.filter(s => s.sector === filters.sector);
    if (filters.minPrice) result = result.filter(s => s.price >= parseFloat(filters.minPrice));
    if (filters.maxPrice) result = result.filter(s => s.price <= parseFloat(filters.maxPrice));
    if (filters.minPE) result = result.filter(s => s.pe_ratio && s.pe_ratio >= parseFloat(filters.minPE));
    if (filters.maxPE) result = result.filter(s => s.pe_ratio && s.pe_ratio <= parseFloat(filters.maxPE));
    if (filters.minROE) result = result.filter(s => s.roe && s.roe >= parseFloat(filters.minROE));
    if (filters.minVolume) result = result.filter(s => s.volume >= parseFloat(filters.minVolume));

    result = applyPresetFilter(result);

    if (activePreset === 'all') {
      result = [...result].sort((a, b) => {
        let aVal: number, bVal: number;
        switch (sortField) {
          case 'code': return sortOrder === 'asc' ? a.code.localeCompare(b.code) : b.code.localeCompare(a.code);
          case 'price': aVal = a.price; bVal = b.price; break;
          case 'change':
            aVal = marketService.calculateChangePercent(a.price, a.prev_close);
            bVal = marketService.calculateChangePercent(b.price, b.prev_close);
            break;
          case 'volume': aVal = a.volume; bVal = b.volume; break;
          case 'market_cap': aVal = a.market_cap; bVal = b.market_cap; break;
          case 'pe_ratio': aVal = a.pe_ratio || 0; bVal = b.pe_ratio || 0; break;
          default: aVal = 0; bVal = 0;
        }
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }

    setFilteredStocks(result);
  };

  const toggleWatchlist = async (stockCode: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    try {
      if (watchlist.includes(stockCode)) {
        await marketService.removeFromWatchlist(user.id, stockCode);
        setWatchlist(watchlist.filter(c => c !== stockCode));
      } else {
        await marketService.addToWatchlist(user.id, stockCode);
        setWatchlist([...watchlist, stockCode]);
      }
    } catch (error) {
      console.error('Error toggling watchlist:', error);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handlePresetSelect = (preset: ScreenerPreset) => {
    setActivePreset(preset);
    setShowPresetDropdown(false);
  };

  const sectors = [...new Set(stocks.map(s => s.sector))];
  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;
  const activePresetInfo = SCREENER_PRESETS.find(p => p.id === activePreset);

  const resetFilters = () => {
    setFilters({ sector: '', minPrice: '', maxPrice: '', minPE: '', maxPE: '', minROE: '', minVolume: '' });
    setActivePreset('all');
  };

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center space-x-1 text-xs font-medium ${
        sortField === field ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'
      }`}
    >
      <span>{label}</span>
      <ArrowUpDown className="w-3 h-3" />
    </button>
  );

  const getSignal = (stock: Stock): 'BUY' | 'SELL' | 'HOLD' => {
    const changePercent = marketService.calculateChangePercent(stock.price, stock.prev_close);
    if (stock.macd_signal === 'bullish' && stock.rsi && stock.rsi < 70 && changePercent > 0) return 'BUY';
    if (stock.macd_signal === 'bearish' && stock.rsi && stock.rsi > 30 && changePercent < 0) return 'SELL';
    return 'HOLD';
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            type="text"
            placeholder="Cari Saham"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-12"
          />
        </div>

        <div className="relative">
          <button
            onClick={() => setShowPresetDropdown(!showPresetDropdown)}
            className={`flex items-center justify-between space-x-3 px-4 py-3 rounded-lg font-medium transition-all min-w-[200px] ${
              activePreset !== 'all'
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5" />
              <span>{activePresetInfo?.name || 'Screener'}</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${showPresetDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showPresetDropdown && (
            <div className="absolute top-full left-0 mt-2 w-72 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
              <div className="max-h-[400px] overflow-y-auto scrollbar-thin">
                {SCREENER_PRESETS.map((preset) => {
                  const Icon = preset.icon;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetSelect(preset.id)}
                      className={`w-full flex items-start space-x-3 px-4 py-3 text-left hover:bg-slate-800 transition-colors ${
                        activePreset === preset.id ? 'bg-emerald-500/10 border-l-2 border-emerald-500' : ''
                      }`}
                    >
                      <Icon className={`w-5 h-5 mt-0.5 ${activePreset === preset.id ? 'text-emerald-400' : 'text-slate-500'}`} />
                      <div>
                        <div className={`font-medium ${activePreset === preset.id ? 'text-emerald-400' : 'text-white'}`}>
                          {preset.name}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{preset.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
            showFilters || activeFiltersCount > 0
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'
          }`}
        >
          <SlidersHorizontal className="w-5 h-5" />
          <span>Filter</span>
          {activeFiltersCount > 0 && (
            <span className="w-5 h-5 bg-emerald-500 text-white rounded-full text-xs flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {activePreset !== 'all' && (
        <div className="flex items-center justify-between px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <div className="flex items-center space-x-2">
            {activePresetInfo && <activePresetInfo.icon className="w-4 h-4 text-emerald-400" />}
            <span className="text-emerald-400 font-medium">{activePresetInfo?.name}</span>
            <span className="text-slate-500">-</span>
            <span className="text-slate-400 text-sm">{filteredStocks.length} saham ditemukan</span>
          </div>
          <button onClick={() => setActivePreset('all')} className="text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showFilters && (
        <div className="card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-white">Filter Tambahan</h3>
            {activeFiltersCount > 0 && (
              <button onClick={resetFilters} className="text-sm text-red-400 hover:text-red-300 flex items-center space-x-1">
                <X className="w-4 h-4" />
                <span>Reset</span>
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">Sektor</label>
              <select
                value={filters.sector}
                onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
                className="input-field text-sm py-2"
              >
                <option value="">Semua</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">Harga Min</label>
              <input type="number" value={filters.minPrice} onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })} placeholder="0" className="input-field text-sm py-2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">Harga Max</label>
              <input type="number" value={filters.maxPrice} onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })} placeholder="Max" className="input-field text-sm py-2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">PE Min</label>
              <input type="number" value={filters.minPE} onChange={(e) => setFilters({ ...filters, minPE: e.target.value })} placeholder="0" className="input-field text-sm py-2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">PE Max</label>
              <input type="number" value={filters.maxPE} onChange={(e) => setFilters({ ...filters, maxPE: e.target.value })} placeholder="Max" className="input-field text-sm py-2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">ROE Min (%)</label>
              <input type="number" value={filters.minROE} onChange={(e) => setFilters({ ...filters, minROE: e.target.value })} placeholder="0" className="input-field text-sm py-2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">Volume Min</label>
              <input type="number" value={filters.minVolume} onChange={(e) => setFilters({ ...filters, minVolume: e.target.value })} placeholder="0" className="input-field text-sm py-2" />
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="px-4 py-3 text-left"><SortButton field="code" label="Saham" /></th>
                  <th className="px-4 py-3 text-right"><SortButton field="price" label="Harga" /></th>
                  <th className="px-4 py-3 text-right"><SortButton field="change" label="Change" /></th>
                  <th className="px-4 py-3 text-right"><SortButton field="volume" label="Volume" /></th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500">Vol Ratio</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500">Foreign</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500">RSI</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500">Signal</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {filteredStocks.map((stock) => {
                  const change = marketService.calculateChange(stock.price, stock.prev_close);
                  const changePercent = marketService.calculateChangePercent(stock.price, stock.prev_close);
                  const isPositive = change >= 0;
                  const signal = getSignal(stock);

                  return (
                    <tr
                      key={stock.id}
                      onClick={() => onStockClick(stock)}
                      className="hover:bg-slate-800/30 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                            <span className="font-bold text-sm text-emerald-400">{stock.code.slice(0, 2)}</span>
                          </div>
                          <div>
                            <div className="font-semibold text-white">{stock.code}</div>
                            <div className="text-xs text-slate-500 truncate max-w-[150px]">{stock.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="font-mono font-semibold text-white">{marketService.formatPrice(stock.price)}</span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className={`flex items-center justify-end space-x-1 ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                          {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          <span className="font-mono font-medium">{isPositive ? '+' : ''}{changePercent.toFixed(2)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right text-slate-400 font-mono text-sm">
                        {marketService.formatLargeNumber(stock.volume)}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className={`font-mono text-sm ${stock.volume_ratio > 1.5 ? 'text-emerald-400' : 'text-slate-400'}`}>
                          {stock.volume_ratio?.toFixed(1)}x
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className={`font-mono text-sm ${stock.foreign_net_buy > 0 ? 'text-emerald-400' : stock.foreign_net_buy < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                          {stock.foreign_net_buy > 0 ? '+' : ''}{marketService.formatLargeNumber(stock.foreign_net_buy)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`font-mono text-sm ${
                          stock.rsi && stock.rsi < 30 ? 'text-emerald-400' :
                          stock.rsi && stock.rsi > 70 ? 'text-red-400' : 'text-slate-400'
                        }`}>
                          {stock.rsi?.toFixed(0) || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          signal === 'BUY' ? 'signal-buy' : signal === 'SELL' ? 'signal-sell' : 'signal-hold'
                        }`}>
                          {signal}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={(e) => toggleWatchlist(stock.code, e)}
                          className={`p-2 rounded-lg transition-all ${
                            watchlist.includes(stock.code)
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-slate-800 text-slate-500 hover:text-amber-400'
                          }`}
                        >
                          <Star className={`w-4 h-4 ${watchlist.includes(stock.code) ? 'fill-current' : ''}`} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && filteredStocks.length === 0 && (
        <div className="card p-12 text-center">
          <Search className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Tidak ada saham yang sesuai dengan filter</p>
          <button onClick={resetFilters} className="mt-4 text-emerald-400 hover:text-emerald-300">
            Reset Filter
          </button>
        </div>
      )}
    </div>
  );
};
